import React, { useRef, useState, useEffect } from "react";
import { Eraser, Check, PenLine } from "lucide-react";
import { Button } from "./ui/button";

// Canvas-based signature pad with explicit "確定" button.
// - User draws freely on the canvas (local only).
// - "確定" button commits the drawn signature as base64 PNG to parent via onChange.
// - "クリア" wipes the canvas and resets confirmation.
// - Once confirmed, the canvas is locked. "再署名" reopens it for redraw.
export default function SignaturePad({ value, onChange, label = "署名", testid, disabled = false }) {
  const canvasRef = useRef(null);
  const ctxRef = useRef(null);
  const drawingRef = useRef(false);
  const lastPointRef = useRef(null);
  const [hasInk, setHasInk] = useState(!!value);
  // confirmed = the current drawing has been saved to parent (value)
  const [confirmed, setConfirmed] = useState(!!value);

  // Initialize canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ratio = window.devicePixelRatio || 1;
    const cssW = canvas.clientWidth;
    const cssH = canvas.clientHeight;
    canvas.width = cssW * ratio;
    canvas.height = cssH * ratio;
    const ctx = canvas.getContext("2d");
    ctx.scale(ratio, ratio);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.lineWidth = 2;
    ctx.strokeStyle = "#1C1C1A";
    ctxRef.current = ctx;

    if (value) {
      const img = new Image();
      img.onload = () => ctx.drawImage(img, 0, 0, cssW, cssH);
      img.src = value;
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const isLocked = disabled || confirmed;

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const isTouch = e.touches && e.touches.length > 0;
    const clientX = isTouch ? e.touches[0].clientX : e.clientX;
    const clientY = isTouch ? e.touches[0].clientY : e.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const start = (e) => {
    if (isLocked) return;
    e.preventDefault();
    drawingRef.current = true;
    lastPointRef.current = getPos(e);
  };

  const move = (e) => {
    if (isLocked) return;
    if (!drawingRef.current) return;
    e.preventDefault();
    const ctx = ctxRef.current;
    const p = getPos(e);
    ctx.beginPath();
    ctx.moveTo(lastPointRef.current.x, lastPointRef.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastPointRef.current = p;
    if (!hasInk) setHasInk(true);
  };

  const end = () => {
    if (isLocked) return;
    drawingRef.current = false;
  };

  const clear = () => {
    if (disabled) return;
    const canvas = canvasRef.current;
    const ctx = ctxRef.current;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasInk(false);
    setConfirmed(false);
    onChange?.("");
  };

  const confirm = () => {
    if (disabled) return;
    if (!hasInk) return;
    const dataUrl = canvasRef.current.toDataURL("image/png");
    setConfirmed(true);
    onChange?.(dataUrl);
  };

  const reedit = () => {
    if (disabled) return;
    setConfirmed(false);
  };

  return (
    <div className="space-y-2" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-[#4A4A48]">{label}</span>
        <div className="flex items-center gap-2 flex-wrap">
          {confirmed && (
            <span
              data-testid={testid ? `${testid}-confirmed-badge` : undefined}
              className="text-[10px] text-[#2C5545] flex items-center gap-0.5 font-numeric bg-[#D9E2D5]/60 border border-[#2C5545]/30 rounded-full px-2 py-0.5"
            >
              <Check size={10} /> 確定済
            </span>
          )}
          {!confirmed && hasInk && (
            <span className="text-[10px] text-[#8B6424] flex items-center gap-0.5 font-numeric bg-[#FFF8E6] border border-[#D4A373]/40 rounded-full px-2 py-0.5">
              未確定
            </span>
          )}
          {!disabled && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={clear}
              data-testid={testid ? `${testid}-clear` : undefined}
              className="h-7 px-2 text-[11px] border-[#E5E5E3]"
            >
              <Eraser size={12} className="mr-1" />
              クリア
            </Button>
          )}
          {!disabled && !confirmed && (
            <Button
              type="button"
              size="sm"
              onClick={confirm}
              disabled={!hasInk}
              data-testid={testid ? `${testid}-confirm` : undefined}
              className="h-7 px-3 text-[11px] bg-[#2C5545] text-white hover:bg-[#1F3D31] disabled:opacity-50"
            >
              <Check size={12} className="mr-1" />
              署名を確定
            </Button>
          )}
          {!disabled && confirmed && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={reedit}
              data-testid={testid ? `${testid}-reedit` : undefined}
              className="h-7 px-2 text-[11px] border-[#A74141] text-[#A74141] hover:bg-[#A74141]/5"
            >
              <PenLine size={12} className="mr-1" />
              再署名
            </Button>
          )}
        </div>
      </div>
      <div
        className={`relative rounded-md border-2 ${
          isLocked
            ? confirmed
              ? "border-solid border-[#2C5545]/40 bg-[#FAFCF8]"
              : "border-solid border-[#E5E5E3] bg-[#F3F3F1]"
            : "border-dashed border-[#2C5545]/30 bg-white"
        }`}
      >
        <canvas
          ref={canvasRef}
          className={`w-full h-32 rounded-md touch-none ${isLocked ? "cursor-not-allowed" : "cursor-crosshair"}`}
          onMouseDown={start}
          onMouseMove={move}
          onMouseUp={end}
          onMouseLeave={end}
          onTouchStart={start}
          onTouchMove={move}
          onTouchEnd={end}
          data-testid={testid ? `${testid}-canvas` : undefined}
        />
        {!hasInk && !disabled && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-[#8A8A86] text-xs">
            ここに指/マウスで署名してください
          </div>
        )}
        {!hasInk && disabled && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-[#8A8A86] text-xs">
            (未署名)
          </div>
        )}
      </div>
    </div>
  );
}
